import MainNav from '../components/MainNav';
import MainFooter from '../components/MainFooter';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, ExternalLink, Phone, Heart, Brain, Users } from 'lucide-react';
import { usePageTracking, trackResourceAccess } from '../hooks/useAnalytics';

const ResourcesPage = () => {
    // Track page view
    usePageTracking('Resources');
    
    const handleResourceClick = (item, category) => {
        trackResourceAccess(item.name, item.url, category);
        window.open(item.url, '_blank');
    };

    const resources = [
        {
            category: 'Mental Health',
            icon: Brain,
            items: [
                { name: 'National Alliance on Mental Illness (NAMI)', url: 'https://www.nami.org/', desc: 'Education, support groups, and advocacy for mental health' },
                { name: 'Therapy for Black Men', url: 'https://therapyforblackmen.org/', desc: 'Connecting Black men with culturally competent therapists' },
                { name: 'Boris Lawrence Henson Foundation', url: 'https://borislhensonfoundation.org/', desc: 'Mental health resources for the African American community' },
            ]
        },
        {
            category: 'Crisis Support',
            icon: Phone,
            items: [
                { name: '988 Suicide & Crisis Lifeline', url: 'tel:988', desc: 'Free, confidential support 24/7. Call or text 988' },
                { name: 'Crisis Text Line', url: 'https://www.crisistextline.org/', desc: 'Text HOME to 741741 for free crisis counseling' },
                { name: 'SAMHSA National Helpline', url: 'tel:1-800-662-4357', desc: '1-800-662-4357 - Treatment referrals and information' },
            ]
        },
        {
            category: 'Wellness & Self-Care',
            icon: Heart,
            items: [
                { name: 'Headspace', url: 'https://www.headspace.com/', desc: 'Guided meditation and mindfulness' },
                { name: 'Calm', url: 'https://www.calm.com/', desc: 'Sleep, meditation, and relaxation' },
                { name: 'Liberate Meditation', url: 'https://liberatemeditation.com/', desc: 'Meditation app for the Black community' },
            ]
        },
        {
            category: 'Community & Brotherhood',
            icon: Users,
            items: [
                { name: '100 Black Men of America', url: 'https://100blackmen.org/', desc: 'Mentoring and community service organization' },
                { name: 'My Brother\'s Keeper Alliance', url: 'https://www.obama.org/mbka/', desc: 'Building pathways to opportunity for boys and young men of color' },
            ]
        }
    ];

    const books = [
        { title: 'The Unapologetic Guide to Black Mental Health', author: 'Rheeda Walker, PhD' },
        { title: 'Post Traumatic Slave Syndrome', author: 'Dr. Joy DeGruy' },
        { title: 'My Grandmother\'s Hands', author: 'Resmaa Menakem' },
        { title: 'The Will to Change: Men, Masculinity, and Love', author: 'bell hooks' },
        { title: 'Hood Feminism', author: 'Mikki Kendall' },
        { title: 'Brother, I\'m Dying', author: 'Edwidge Danticat' },
    ];

    return (
        <div className="min-h-screen bg-[#3D4435]">
            <MainNav />
            
            <main className="pt-32 pb-24 px-6">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-4">
                            In The Community
                        </p>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Playfair_Display'] mb-6">
                            Resources
                        </h1>
                        <p className="text-[#A0A0A0] text-lg max-w-2xl mx-auto">
                            Helpful resources for mental wellness, self-care, and community support
                        </p>
                    </div>

                    {/* Resource Categories */}
                    <div className="space-y-12 mb-16">
                        {resources.map((category, idx) => (
                            <section key={idx}>
                                <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-6 flex items-center gap-3">
                                    <category.icon className="w-6 h-6 text-[#C9A227]" />
                                    {category.category}
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {category.items.map((item, itemIdx) => (
                                        <Card key={itemIdx} className="bg-[#4A5240] border-[#5A6350] hover:border-[#C9A227]/50 transition-all">
                                            <CardContent className="p-6">
                                                <h3 className="text-white font-semibold mb-2">{item.name}</h3>
                                                <p className="text-[#A0A0A0] text-sm mb-4">{item.desc}</p>
                                                <Button
                                                    variant="ghost"
                                                    className="text-[#C9A227] hover:text-[#E8D48A] p-0 h-auto"
                                                    onClick={() => handleResourceClick(item, category.category)}
                                                >
                                                    Visit Resource
                                                    <ExternalLink className="w-4 h-4 ml-2" />
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>

                    {/* Recommended Reading */}
                    <section>
                        <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-6 flex items-center gap-3">
                            <BookOpen className="w-6 h-6 text-[#C9A227]" />
                            Recommended Reading
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {books.map((book, idx) => (
                                <Card key={idx} className="bg-[#4A5240] border-[#5A6350]">
                                    <CardContent className="p-5">
                                        <h3 className="text-white font-medium mb-1">{book.title}</h3>
                                        <p className="text-[#C9A227] text-sm">by {book.author}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </section>

                    {/* Disclaimer */}
                    <div className="mt-16 p-6 bg-[#4A5240] rounded-xl border border-[#5A6350]">
                        <p className="text-[#A0A0A0] text-sm">
                            <strong className="text-white">Note:</strong> These resources are provided for informational purposes only. If you are experiencing a mental health crisis, please contact emergency services or call 988 for immediate support. Recovery Room is not a substitute for professional mental health treatment.
                        </p>
                    </div>
                </div>
            </main>

            <MainFooter />
        </div>
    );
};

export default ResourcesPage;
